import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
import { FACILITY_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import {
  Facility,
  FacilitySortOrQuery,
} from 'src/app/model/locations/facility';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { FacilityTableService } from 'src/app/services/table/facility-table.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { FACILITY_TABLE_DEFINITION } from '../../definitions/facility-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { BusinessUnitFilterComponent } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Panel } from '@openng/optimus-ui/panel';
import { FacilityMobileFilterComponent } from '../../components/facility-mobile-filter/facility-mobile-filter.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { FacilitySearchResult } from 'src/app/model/locations/facility-search-results';

@Component({
  selector: 'app-facility-container',
  templateUrl: './facility-container.component.html',
  styleUrls: ['./facility-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent,
    ToggleSwitch,
    FormsModule,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    Panel,
    FacilityMobileFilterComponent,
    TranslatePipe,
  ],
})
export class FacilityContainerComponent
  extends SearchContainerComponent<FacilitySearchResult>
  implements OnInit, OnDestroy, AfterViewInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _facilityApi = inject(FacilityApiService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _facilityTableService = inject(FacilityTableService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  allFacs: any[] = [];
  displayDialog = false;
  states: any[] = [];
  filteredStates: any[] = [];
  counties: any[] = [];
  filteredCounties: any[] = [];
  townships: any[] = [];
  filteredTownships: any[] = [];
  timerSub?: Subscription;
  facilitiesLoadingEvent = new EventEmitter<any>();
  allFacilities: Facility[] = [];
  canCreate = false;
  canEdit = false;
  sub?: Subscription;
  mobileFilterCollapsed = true;
  refreshSubscription?: Subscription;
  showHiddenFacilities: boolean = false;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._facilityTableService);
    this._translateService
      .get('LOCATION.SCREEN.FACILITY')
      .subscribe((label) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb({
            label: label,
            routerLink: null,
          });
        } else {
          this._tabService.updateActiveTabLabel(label);
        }
        this.screenName = label;
      });
    this.columns = FACILITY_TABLE_DEFINITION;

    this.initialize();

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  initialize() {
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.CREATE_FACILITIES,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.FACILITIES.EDIT_FACILITIES,
    ]);
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }

    this.refreshSubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.applyPreviousFilters();
    this.applyPreviousData();
  }

  /**
   * Method called when the user hits the clear button.
   */
  clearTable() {
    this.clearScreen();
    this.showHiddenFacilities = false;
    this.allFacilities = [];
  }

  newFacility() {
    const tab = this._tabService.buildNewTab(FACILITY_DETAIL_CONTAINER, true);
    this._tabService.openTab(tab);
  }

  changeViewEvent($event: { data: Facility }) {
    const tab = this._tabService.buildNewTab(
      FACILITY_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id!
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'facilityName');

    this._facilityApi
      .queryForFacilities(
        req.pageSize,
        req.page,
        query as FacilitySortOrQuery,
        sort
      )
      .subscribe({
        next: (facilityConnection) => {
          if (facilityConnection) {
            this.elements = facilityConnection.items;
            this.totalRecords = facilityConnection.totalRecords;
            this._facilityTableService.setLastTotalRecords(this.totalRecords);
            this.loading = false;
          }
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'LOCATION.MESSAGES.ERROR.LOAD_FACILITIES',
          });
          this.loading = false;
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as unknown as any;
    filters['hidden'] = this.showHiddenFacilities as unknown as any;

    const query = this._facilityBuilder.buildFacilityQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._facilityTableService.getLastLazyLoad()! });
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        facilityName: $event.facilityName
          ? {
              value: $event.facilityName,
              matchMode: 'contains',
            }
          : null,
        state: $event.state
          ? {
              value: $event.state,
              matchMode: 'contains',
            }
          : null,
        county: $event.county
          ? {
              value: $event.county,
              matchMode: 'contains',
            }
          : null,
        township: $event.township
          ? {
              value: $event.township,
              matchMode: 'contains',
            }
          : null,
        hidden: $event.hidden
          ? {
              value: $event.hidden,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._facilityTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._facilityTableService.getLastLazyLoad()! });
  }

  refresh(setLoading = true): void {
    this.lazyLoad(
      { lazy: this._facilityTableService.getLastLazyLoad()! },
      setLoading
    );
  }
}
